import React, { useEffect, useCallback } from 'react';
import { Link,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams } from 'react-router';

import { useLingui } from '@lingui/react/macro';
import { CaretLeftIcon } from '@patternfly/react-icons';
import { Card, PageSection } from '@patternfly/react-core';

import useRequest from 'hooks/useRequest';
import { InstanceGroupsAPI } from 'api';
import RoutedTabs from 'components/RoutedTabs';
import ContentError from 'components/ContentError';
import ContentLoading from 'components/ContentLoading';
import JobList from 'components/JobList';

import ContainerGroupDetails from './ContainerGroupDetails';
import ContainerGroupEdit from './ContainerGroupEdit';

function ContainerGroup({ setBreadcrumb }) {
  const { t } = useLingui();
  const { id } = useParams();
  const { pathname } = useLocation();

  const {
    isLoading,
    error: contentError,
    request: fetchInstanceGroups,
    result: { instanceGroup },
  } = useRequest(
    useCallback(async () => {
      const { data } = await InstanceGroupsAPI.readDetail(id);
      return {
        instanceGroup: data,
      };
    }, [id]),
    { instanceGroup: null }
  );

  useEffect(() => {
    fetchInstanceGroups();
  }, [fetchInstanceGroups, pathname]);

  useEffect(() => {
    if (instanceGroup) {
      setBreadcrumb(instanceGroup);
    }
  }, [instanceGroup, setBreadcrumb]);

  const tabsArray = [
    {
      name: (
        <>
          <CaretLeftIcon />
          {t`Back to instance groups`}
        </>
      ),
      link: '/instance_groups',
      id: 99,
    },
    {
      name: t`Details`,
      link: `/instance_groups/container_group/${id}/details`,
      id: 0,
    },
    {
      name: t`Jobs`,
      link: `/instance_groups/container_group/${id}/jobs`,
      id: 1,
    },
  ];

  if (!isLoading && contentError) {
    return (
      <PageSection hasBodyWrapper={false}>
        <Card>
          <ContentError error={contentError}>
            {contentError.response?.status === 404 && (
              <span>
                {t`Container group not found.`}

                <Link to="/instance_groups">
                  {t`View all instance groups`}
                </Link>
              </span>
            )}
          </ContentError>
        </Card>
      </PageSection>
    );
  }

  let cardHeader = <RoutedTabs tabsArray={tabsArray} />;
  if (pathname.endsWith('edit')) {
    cardHeader = null;
  }

  return (
    <PageSection hasBodyWrapper={false}>
      <Card>
        {cardHeader}
        {isLoading && <ContentLoading />}
        {!isLoading && instanceGroup && (
          <Routes>
            <Route index element={<Navigate to="details" replace />} />
            <Route
              path="edit"
              element={<ContainerGroupEdit instanceGroup={instanceGroup} />}
            />
            <Route
              path="details"
              element={<ContainerGroupDetails instanceGroup={instanceGroup} />}
            />
            <Route
              path="jobs"
              element={
                <JobList
                  showTypeColumn
                  defaultParams={{ instance_group: instanceGroup.id }}
                />
              }
            />
            <Route
              path="*"
              element={
                <ContentError isNotFound>
                  <Link to="/instance_groups">
                    {t`View all instance groups`}
                  </Link>
                </ContentError>
              }
            />
          </Routes>
        )}
      </Card>
    </PageSection>
  );
}

export default ContainerGroup;
